import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Camera, X, Minus, Plus, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "./ui/sheet";
import { api, img, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";

import { tx, useLang } from "../lib/i18n";
const panel = "bg-[var(--mv-card)] border-[var(--mv-border-2)] text-[var(--mv-text)]";

export const AuthGate = () => { useLang();
  const { gate, setGate, t } = useApp();
  const nav = useNavigate();
  const go = (path) => { setGate(false); nav(path); };
  return (
    <Dialog open={gate} onOpenChange={setGate}>
      <DialogContent className={`${panel} max-w-md`} data-testid="auth-gate-dialog">
        <div className="grid h-12 w-12 place-items-center rounded-full bg-[var(--mv-teal-dim)]"><Lock className="h-5 w-5 text-[var(--mv-teal)]" /></div>
        <DialogTitle className="text-xl">{t("auth.gateTitle")}</DialogTitle>
        <DialogDescription className="text-[var(--mv-muted)]">{t("auth.gateBody")}</DialogDescription>
        <div className="mt-2 grid grid-cols-2 gap-3">
          <button className="mv-btn-primary rounded-xl py-3 text-sm" data-testid="auth-gate-signin" onClick={() => go("/login")}>{t("auth.signIn")}</button>
          <button className="mv-btn-outline rounded-xl py-3 text-sm" data-testid="auth-gate-signup" onClick={() => go("/login?mode=register")}>{t("auth.signUp")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const SearchDialog = () => { useLang();
  const { searchOpen, setSearchOpen, t, requireAuth, formatPrice } = useApp();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef();
  const nav = useNavigate();

  useEffect(() => {
    if (!q.trim()) { setRes(null); return; }
    setLoading(true);
    const id = setTimeout(() => api.get("/products", { params: { q, limit: 8 } }).then((r) => setRes(r.data)).finally(() => setLoading(false)), 250);
    return () => clearTimeout(id);
  }, [q]);

  const camera = () => {
    if (!requireAuth()) { setSearchOpen(false); return; }
    fileRef.current?.click();
  };
  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/search/image", fd);
      toast.info(tx(data.message));
    } catch (err) { toast.error(tx(errMsg(err))); }
    e.target.value = "";
  };
  const submit = (e) => { e.preventDefault(); if (q.trim()) { setSearchOpen(false); nav(`/shop?q=${encodeURIComponent(q.trim())}`); } };

  return (
    <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
      <DialogContent className={`${panel} max-w-2xl p-0 overflow-hidden`} data-testid="search-dialog">
        <DialogTitle className="sr-only">{t("common.search")}</DialogTitle>
        <form onSubmit={submit} className="flex items-center gap-3 border-b border-[var(--mv-border-2)] px-5 py-4">
          <Search className="h-5 w-5 text-[var(--mv-muted)]" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.searchPlaceholder")} data-testid="search-input"
            className="flex-1 bg-transparent text-base outline-none placeholder:text-[var(--mv-dim)]" />
          <button type="button" onClick={camera} data-testid="search-camera-button" aria-label={t("common.camera")} className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/5">
            <Camera className="h-5 w-5 text-[var(--mv-teal)]" />
          </button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onPhoto} data-testid="search-camera-input" />
        </form>
        <div className="max-h-[60vh] overflow-y-auto p-3" data-testid="search-results">
          {tx(loading && <p className="p-4 text-sm text-[var(--mv-muted)]">{t("common.loading")}</p>)}
          {tx(!loading && res && res.items.length === 0 && <p className="p-4 text-sm text-[var(--mv-muted)]" data-testid="search-no-results">{t("common.noResults")}</p>)}
          {tx(!loading && res?.items.map((p) => (
            <Link key={p.product_id} to={`/product/${p.slug}`} onClick={() => setSearchOpen(false)} data-testid={`search-result-${p.slug}`}
              className="flex items-center gap-4 rounded-xl p-2.5 hover:bg-white/5">
              <img src={img(p.image)} alt="" className="h-14 w-14 rounded-lg mv-card-bg object-contain" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{p.title}</p>
                <p className="text-xs text-[var(--mv-dim)]">{p.item_number} · {p.sku}</p>
              </div>
              <span className="text-sm text-[var(--mv-muted)]">{formatPrice(p.price_cents)}</span>
            </Link>
          )))}
          {tx(!q && <p className="p-4 text-xs text-[var(--mv-dim)]">{t("common.searchPlaceholder")}</p>)}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const QtyStepper = ({ value, min, max, onChange, testid }) => { useLang(); return (
  <div className="flex items-center rounded-full border border-[var(--mv-border-2)]" data-testid={testid}>
    <button className="grid h-8 w-8 place-items-center disabled:opacity-30" disabled={value <= min} onClick={() => onChange(value - 1)} data-testid={`${testid}-minus`} aria-label={tx("Decrease")}><Minus className="h-3.5 w-3.5" /></button>
    <span className="w-8 text-center text-sm" data-testid={`${testid}-value`}>{value}</span>
    <button className="grid h-8 w-8 place-items-center disabled:opacity-30" disabled={value >= max} onClick={() => onChange(value + 1)} data-testid={`${testid}-plus`} aria-label={tx("Increase")}><Plus className="h-3.5 w-3.5" /></button>
  </div>
); };
export { QtyStepper };

export const CartDrawer = () => { const lang = useLang();
  const { cartOpen, setCartOpen, t, formatPrice, setCounts, user } = useApp();
  const [cart, setCart] = useState(null);
  const load = (data) => { setCart(data); setCounts((c) => ({ ...c, cart: data.count })); };
  useEffect(() => { if (cartOpen && user) api.get("/cart").then((r) => load(r.data)); }, [cartOpen, user, lang]); // eslint-disable-line
  const setQty = async (line, q) => { try { load((await api.patch(`/cart/items/${line}`, { quantity: q })).data); } catch (e) { toast.error(tx(errMsg(e))); } };
  const remove = async (line) => load((await api.delete(`/cart/items/${line}`)).data);
  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent side="right" className={`${panel} w-full sm:max-w-md flex flex-col`} data-testid="cart-drawer">
        <SheetTitle className="text-lg text-[var(--mv-text)]">{t("cart.title")}</SheetTitle>
        <div className="flex-1 overflow-y-auto -mx-2 px-2 mt-4 space-y-4">
          {tx(cart?.items.length === 0 && <p className="text-sm text-[var(--mv-muted)]" data-testid="cart-empty">{t("cart.empty")}</p>)}
          {cart?.items.map((l) => (
            <div key={l.line_id} className="flex gap-3" data-testid={`cart-line-${l.sku}`}>
              <img src={img(l.image)} alt="" className="h-20 w-20 rounded-xl mv-card-bg object-contain" />
              <div className="flex-1 min-w-0">
                <Link to={`/product/${l.slug}`} onClick={() => setCartOpen(false)} className="block truncate text-sm font-semibold">{l.title}</Link>
                <p className="text-xs text-[var(--mv-dim)]">{Object.values(l.options).join(" / ")} · {l.sku}</p>
                <div className="mt-2 flex items-center justify-between">
                  <QtyStepper value={l.quantity} min={l.moq} max={l.stock} onChange={(q) => setQty(l.line_id, q)} testid={`cart-qty-${l.sku}`} />
                  <span className="text-sm">{formatPrice(l.line_total_cents)}</span>
                </div>
              </div>
              <button onClick={() => remove(l.line_id)} aria-label={t("cart.remove")} data-testid={`cart-remove-${l.sku}`} className="self-start p-1 text-[var(--mv-dim)] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        {tx(cart?.items.length > 0 && (
          <div className="border-t border-[var(--mv-border-2)] pt-4 space-y-3">
            <div className="flex justify-between text-sm"><span className="text-[var(--mv-muted)]">{t("cart.subtotal")}</span><span data-testid="cart-subtotal">{formatPrice(cart.subtotal_cents)}</span></div>
            <Link to="/checkout" onClick={() => setCartOpen(false)} className="block w-full mv-btn-primary rounded-xl py-3 text-center text-sm" data-testid="cart-checkout-button">{t("cart.checkout")}</Link>
            <p className="text-center text-[10px] text-[var(--mv-dim)]">{tx("First-order coupons apply automatically at checkout")}</p>
          </div>
        ))}
        <button onClick={() => setCartOpen(false)} className="sr-only">{t("common.close")}<X /></button>
      </SheetContent>
    </Sheet>
  );
};
